import { useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { ToolField } from '@/modules/back-testing/datasets/tools/tool-schema';

const NUMBER = /^-?\d+(\.\d+)?$/;
const BOX = 'h-5 rounded-md border bg-white px-1.5 font-mono text-[11px] leading-4 text-ink outline-none focus:border-indigo-500';

type Value = string | number | boolean;

interface FixedValueProps {
	field: ToolField;
	value: Value;
	onChange: (value: Value) => void;
	/** Set when the value was just chosen in the picker, so typing can start right away. */
	focusOnMount?: boolean;
	className?: string;
}

/** A fixed value: yes or no, one of the tool's choices, or text (a number for a number field). */
export const FixedValue = ({ field, value, onChange, focusOnMount = false, className }: FixedValueProps) => {
	const numeric = field.kind === 'number' || field.kind === 'integer';
	// A number field holds a number once the text is one, and the text until then.
	const parse = (raw: string): Value => (numeric && NUMBER.test(raw.trim()) ? Number(raw) : raw);
	// Typed text stays as typed, so `1.` on the way to `1.5` is not cut back to `1`.
	const [text, setText] = useState(() => String(value));
	// A change from outside the box, such as undo, replaces the text; the box's own edits do not.
	const [seen, setSeen] = useState(value);
	if (value !== seen) {
		setSeen(value);
		if (parse(text) !== value) setText(String(value));
	}
	const focused = useRef(false);
	const focusFirstTime = (element: HTMLElement | null) => {
		if (!element || !focusOnMount || focused.current) return;
		focused.current = true;
		element.focus();
	};
	const label = `${field.name} value`;

	if (field.kind === 'boolean' || field.kind === 'choice') {
		const options = field.kind === 'boolean' ? [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] : field.options.map((option) => ({ value: option, label: option }));
		return (
			<select
				ref={focusFirstTime}
				aria-label={label}
				className={cn(BOX, 'max-w-36 border-gray-500', className)}
				value={String(value)}
				onChange={(event) => onChange(field.kind === 'boolean' ? event.target.value === 'true' : event.target.value)}
			>
				{field.kind === 'choice' && (
					<option value='' disabled>
						Pick
					</option>
				)}
				{options.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		);
	}

	const invalid = numeric && text.trim() !== '' && !NUMBER.test(text.trim());
	return (
		<input
			ref={focusFirstTime}
			aria-label={label}
			aria-invalid={invalid}
			title={invalid ? 'Not a number' : undefined}
			placeholder='Value'
			inputMode={numeric ? 'decimal' : undefined}
			className={cn(BOX, 'w-32 min-w-0', invalid ? 'border-red-400' : 'border-gray-500', className)}
			value={text}
			onChange={(event) => {
				const next = event.target.value;
				setText(next);
				onChange(parse(next));
			}}
		/>
	);
};
